// src/components/EventCountdown.js
// Live countdown to an event start date. Ticks every second and shows
// days / hours / minutes / seconds. Localised labels via react-i18next with
// inline French fallbacks (works before translations are added).
//
// ───────────────────────────────────────────────────────────────────────────
// WHAT WAS WRONG
//
// 1. `const target = date ? new Date(date).getTime() : 0` produced NaN for any
//    date the browser could not parse, and the guard below it was
//    `if (!target) return;`. `!NaN` is true, so the interval was never
//    created: the countdown was a still image, not a countdown.
//
// 2. With target = NaN, getRemaining() computed diff = NaN, and `NaN <= 0` is
//    false — so it fell through to the arithmetic branch and every unit came
//    out NaN. The card printed "NaN : NaN : NaN : NaN".
//
//    This is not hypothetical. Laravel returns a datetime column that is not
//    in $casts as "2026-09-15 09:00:00". Chrome and Firefox tolerate the
//    space; Safari and iOS WebKit return Invalid Date, because only the
//    ISO-8601 form with a "T" is required by the spec. So the countdown broke
//    on iPhone while looking fine on a desktop — parseDate() below normalises
//    the string before handing it to Date.
//
// 3. When `date` was absent, target was 0, the interval was skipped, and the
//    component rendered a frozen 00:00:00:00 that never resolved to "Terminé"
//    (the ongoing/ended branch is guarded by `if (target && ...)`). It renders
//    nothing at all now rather than a fake zero.
//
// 4. isOngoing / isPast were read from Date.now() during render, but nothing
//    re-rendered the component once it had reached zero. The tick now carries
//    `now` in state, so an event flips from countdown to "En cours" to
//    "Terminé" on its own.
//
// 5. `onComplete` sat in the effect dependency array. Any caller passing an
//    inline arrow rebuilt the interval on every render, resetting the seconds.
//    It is held in a ref now.
// ───────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

/* Accepts what the API actually sends and returns a timestamp in ms, or NaN.
   Handled:
     2026-09-15T09:00:00Z          ISO with zone
     2026-09-15T09:00:00+01:00     ISO with offset
     2026-09-15 09:00:00           MySQL / Laravel default  (fails in Safari)
     2026-09-15 09:00:00.000000    Laravel with microseconds
     2026-09-15                    date only
     1789462800000 / 1789462800    epoch ms or seconds
   Anything else, including "0000-00-00 00:00:00", returns NaN. */
export function parseDate(value) {
  if (value == null || value === "") return NaN;

  if (value instanceof Date) return value.getTime();

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return NaN;
    // Ten-digit values are epoch seconds, not milliseconds.
    return value < 1e11 ? value * 1000 : value;
  }

  let s = String(value).trim();
  if (!s) return NaN;

  // A zero date from MySQL is a placeholder, not a time.
  if (/^0{4}-0{2}-0{2}/.test(s)) return NaN;

  if (/^\d+$/.test(s)) return parseDate(Number(s));

  // Drop fractional seconds — Safari rejects more than three digits.
  s = s.replace(/(\d{2}:\d{2}:\d{2})\.\d+/, "$1");

  // "2026-09-15 09:00:00" -> "2026-09-15T09:00:00"
  s = s.replace(/^(\d{4}-\d{2}-\d{2})[ tT](\d{2}:\d{2})/, "$1T$2");

  let t = new Date(s).getTime();
  if (Number.isFinite(t)) return t;

  // Last resort: build it from the parts, read as local time. Reaching here
  // means the string was not something Date understood in this engine.
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (m) {
    t = new Date(
      Number(m[1]), Number(m[2]) - 1, Number(m[3]),
      Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0)
    ).getTime();
    if (Number.isFinite(t)) return t;
  }

  return NaN;
}

function getRemaining(target, now) {
  const diff = target - now;
  if (!Number.isFinite(diff) || diff <= 0) {
    return { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  return {
    total: diff,
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  };
}

/**
 * Props:
 *   - date: start date (event.date_debut) — ISO, MySQL datetime or epoch
 *   - dateEnd: optional end date (event.date_fin) — used to detect "ongoing"
 *   - size: "sm" | "md" | "lg" (default "md")
 *   - onComplete: optional callback fired once when it reaches zero
 */
export default function EventCountdown({ date, dateEnd, size = "md", onComplete }) {
  const { t } = useTranslation();

  const target = useMemo(() => parseDate(date), [date]);
  const endTarget = useMemo(() => parseDate(dateEnd), [dateEnd]);

  const hasTarget = Number.isFinite(target);
  const hasEnd = Number.isFinite(endTarget);

  const [now, setNow] = useState(() => Date.now());

  /* Held in a ref so an inline arrow from the caller cannot restart the
     interval and reset the seconds on every parent render. */
  const doneRef = useRef(onComplete);
  useEffect(() => { doneRef.current = onComplete; }, [onComplete]);

  const firedRef = useRef(false);
  // Reset the "reached zero" latch if the component is pointed at a new event.
  useEffect(() => { firedRef.current = false; }, [target]);

  useEffect(() => {
    if (!hasTarget) return undefined;

    setNow(Date.now());

    const id = setInterval(() => {
      const stamp = Date.now();
      setNow(stamp);

      if (stamp >= target && !firedRef.current) {
        firedRef.current = true;
        if (typeof doneRef.current === "function") doneRef.current();
      }

      /* Once the event is over there is nothing left to count, so stop waking
         up every second — a listing page can hold twenty of these. */
      const finished = hasEnd ? stamp > endTarget : stamp > target;
      if (finished) clearInterval(id);
    }, 1000);

    return () => clearInterval(id);
  }, [target, endTarget, hasTarget, hasEnd]);

  /* No usable date: render nothing. The old code showed a frozen 00:00:00:00,
     which reads as "starting right now" — worse than no countdown at all. */
  if (!hasTarget) return null;

  const remaining = getRemaining(target, now);

  if (remaining.total <= 0) {
    const ongoing = hasEnd ? now <= endTarget : false;
    if (ongoing) {
      return (
        <div className={`evt-countdown evt-countdown-${size} evt-countdown-live`}>
          <span className="evt-live-dot" /> {t("events.ongoing", "En cours")}
        </div>
      );
    }
    return (
      <div className={`evt-countdown evt-countdown-${size} evt-countdown-ended`}>
        {t("events.ended", "Terminé")}
      </div>
    );
  }

  const units = [
    { value: remaining.days, label: t("events.days", "Jours") },
    { value: remaining.hours, label: t("events.hours", "Heures") },
    { value: remaining.minutes, label: t("events.minutes", "Min") },
    { value: remaining.seconds, label: t("events.seconds", "Sec") },
  ];

  const pad = (n) => String(Math.max(0, Number(n) || 0)).padStart(2, "0");

  return (
    <div className={`evt-countdown evt-countdown-${size}`} dir="ltr" aria-label="countdown">
      {units.map((u, i) => (
        <React.Fragment key={u.label}>
          <div className="evt-cd-unit">
            <span className="evt-cd-value">{pad(u.value)}</span>
            <span className="evt-cd-label">{u.label}</span>
          </div>
          {i < units.length - 1 && <span className="evt-cd-sep">:</span>}
        </React.Fragment>
      ))}
    </div>
  );
}
